import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useSeo } from '../hooks/useSeo';
import { interviewsIndex, interviewsPageContent } from '../data/interviews';
import Header from '../components/Header';
import Footer from '../components/Footer';

const InterviewCard = ({ item, language, t, index }) => (
  <Link
    to={item.slug}
    data-testid={`interviews-card-${index + 1}`}
    className="group block border border-[#3f4816]/70 bg-[#302f2c]/60 rounded-sm overflow-hidden hover:border-[#d9fb06]/60 transition-colors duration-300"
  >
    <div className="grid grid-cols-1 md:grid-cols-12">
      <div className="md:col-span-5 relative overflow-hidden aspect-[3/2] md:aspect-auto md:min-h-[18rem] bg-[#0f1110]">
        <img
          src={item.photo}
          alt={item.person}
          loading="lazy"
          className={`w-full h-full object-cover ${item.photoPosition} group-hover:scale-[1.03] transition-transform duration-500`}
        />
        {item.isNew && (
          <span className="absolute top-4 left-4 bg-[#d9fb06] text-[#1a1c1b] text-[0.65rem] font-black tracking-[0.2em] uppercase px-3 py-1.5 rounded-full">
            {t.badgeNew}
          </span>
        )}
      </div>
      <div className="md:col-span-7 p-7 md:p-10 flex flex-col">
        <span className="text-[#888680] text-xs uppercase tracking-[0.25em] mb-4">{item.date[language]}</span>
        <h2 className="font-black text-white text-xl md:text-2xl lg:text-[1.75rem] leading-tight mb-3 group-hover:text-[#d9fb06] transition-colors">
          {item.title[language]}
        </h2>
        <p className="text-[#d9fb06] text-sm font-semibold mb-1">{item.person}</p>
        <p className="text-[#888680] text-sm mb-5">{item.role[language]}</p>
        <p className="text-[#c9c7c0] text-sm md:text-base leading-relaxed mb-6 line-clamp-4">{item.excerpt[language]}</p>
        <span className="mt-auto inline-flex items-center gap-2 text-[#d9fb06] text-sm font-bold uppercase tracking-wider">
          {t.read}
          <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
        </span>
      </div>
    </div>
  </Link>
);

const InterviewsPage = () => {
  const { language } = useLanguage();
  const t = interviewsPageContent[language] || interviewsPageContent.fr;

  useSeo({
    title: t.seoTitle,
    description: t.seoDescription,
    url: 'https://bcfl.eu/interviews',
    image: interviewsIndex[0].photo,
    imageAlt: interviewsIndex[0].person,
    language,
    type: 'website',
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <div className="min-h-screen bg-[#1a1c1b] text-white" data-testid="interviews-page">
      <Header />
      <main className="pt-32 md:pt-36 pb-24">
        <section className="max-w-[87.5rem] mx-auto px-6 md:px-10">
          <Link
            to="/"
            data-testid="interviews-back-link"
            className="group inline-flex items-center gap-2 text-[#888680] hover:text-[#d9fb06] transition-colors text-sm uppercase tracking-wider mb-10"
          >
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
            {t.ctaBack}
          </Link>

          <div className="flex items-center gap-3 mb-5">
            <div className="h-px w-10 bg-[#d9fb06]" />
            <span className="text-[#d9fb06] text-xs tracking-[0.25em] uppercase font-semibold">{t.eyebrow}</span>
          </div>
          <h1 data-testid="interviews-title" className="font-black text-white text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95] mb-6">
            {t.title}
          </h1>
          <p className="text-[#c9c7c0] text-base md:text-lg leading-relaxed max-w-3xl mb-4">{t.subtitle}</p>
          <p data-testid="interviews-count" className="text-[#888680] text-xs uppercase tracking-[0.25em] mb-14">
            {t.count(interviewsIndex.length)}
          </p>

          <div className="space-y-8 max-w-5xl">
            {interviewsIndex.map((item, idx) => (
              <InterviewCard key={item.slug} item={item} language={language} t={t} index={idx} />
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default InterviewsPage;
